import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { Notification } from "@contracts";
import { Button } from "@components/ui/Button";
import { CountBadge } from "@components/ui/Badge";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "@features/notifications/useNotifications";
import { ageOf, BELL_TEXT, KIND_TITLE, pathOf } from "./texts";
import { BadgeSpot, Container, Empty, Item, ItemAge, ItemTitle, List, Panel, PanelHeader, Title, Trigger } from "./style";

function IconBell() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

/**
 * The bell (spec 20261028-132500 AC-8 to AC-10): on every page of the authenticated layout, the unread count in the
 * design system's solid count, refreshed every 60 s and on focus. A notice opens its record and is marked read. It
 * reads the core's notices; the legacy `/internal/carlao/notifications` is no longer called.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const query = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unread = query.data?.unread ?? 0;
  const items = query.data?.items ?? [];

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openNotice = (notice: Notification) => {
    if (!notice.readAt) markRead.mutate(notice.id);
    setOpen(false);
    navigate(pathOf(notice));
  };

  return (
    <Container ref={containerRef}>
      <Trigger
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread > 0 ? `${BELL_TEXT.title}, ${unread} ${BELL_TEXT.unread}` : BELL_TEXT.title}
        onClick={() => setOpen((current) => !current)}
      >
        <IconBell />
        <BadgeSpot aria-hidden="true">
          <CountBadge count={unread} label={BELL_TEXT.unread} />
        </BadgeSpot>
      </Trigger>

      {open && (
        <Panel role="dialog" aria-label={BELL_TEXT.title}>
          <PanelHeader>
            <Title>{BELL_TEXT.title}</Title>
            {unread > 0 && (
              <Button type="button" variant="link" onClick={() => markAll.mutate()}>
                {BELL_TEXT.readAll}
              </Button>
            )}
          </PanelHeader>
          {query.isError ? (
            <Empty role="alert">
              {BELL_TEXT.loadError}{" "}
              <Button type="button" variant="link" onClick={() => void query.refetch()}>
                {BELL_TEXT.retry}
              </Button>
            </Empty>
          ) : query.isPending ? (
            <Empty aria-busy="true">…</Empty>
          ) : items.length === 0 ? (
            <Empty>{BELL_TEXT.empty}</Empty>
          ) : (
            <List>
              {items.map((notice) => (
                <li key={notice.id}>
                  <Item type="button" $unread={!notice.readAt} onClick={() => openNotice(notice)}>
                    <ItemTitle $unread={!notice.readAt}>{KIND_TITLE[notice.kind]}</ItemTitle>
                    <ItemAge>{ageOf(notice.createdAt)}</ItemAge>
                  </Item>
                </li>
              ))}
            </List>
          )}
        </Panel>
      )}
    </Container>
  );
}
